import { store } from '../../redux/store';
import { SubscriptionBillingCycleEnum, SubscriptionPlanCategoryEnum } from '../enums/PartnerSubscription';
import { PaymentStatusEnum } from '../enums/PaymentStatus';
import { PaymentStepsEnum } from '../enums/PaymentSteps';
import { Partner, PartnerUser } from '../interfaces/partner/PartnerProfile';
import { SubscriptionPlanInterface } from '../interfaces/payment/SubscrptionPlanInterface';
import { SubscriptionPurchase } from '../interfaces/product/ProductPurchase';
import { ApplicantListItem } from '../interfaces/applicants/ApplicantListItem';
import { MarketingEvents, marketingEventsWithLastUtm } from './marketing-events';
import { buildFunnelParams } from './funnel-context';
import { FrontendPaymentSource } from '../enums/FrontendPaymentSource';
import { buildApplicantActionMetadata, buildAiInterviewRequestedMetadata, buildCvDatabaseSearchedMetadata, buildPlanSelectedMetadata, buildRegistrationAbandonedMetadata, buildSubscriptionMetadata, buildSubscriptionMetadataFromPurchase, getCompanyId, getLastUtmEventMetadata, getPartnerUserId, hashPiiValue, normalizeMobileToE164 } from './marketing-helpers';

const DUPLICATE_EVENT_WINDOW_MS = 5000;
const recentMarketingEvents = new Map<string, number>();

const withLastUtmMetadata = (eventTitle: string, metadata?: Record<string, unknown>) => {
    if (!marketingEventsWithLastUtm.has(eventTitle)) return metadata;

    const lastUtm = getLastUtmEventMetadata();
    if (!Object.keys(lastUtm).length) return metadata;

    return {
        ...lastUtm,
        ...metadata,
    };
};

const stableStringify = (value: unknown): string => {
    if (value === undefined) return 'undefined';
    if (value === null || typeof value !== 'object') return JSON.stringify(value);

    if (Array.isArray(value)) {
        return `[${value.map((item) => stableStringify(item)).join(',')}]`;
    }

    return `{${Object.keys(value as Record<string, unknown>)
        .sort()
        .map((key) => `${JSON.stringify(key)}:${stableStringify((value as Record<string, unknown>)[key])}`)
        .join(',')}}`;
};

/** Drops the same event name + payload inside the window. A different payload still sends. */
const claimMarketingEventSend = (eventTitle: string, params: Record<string, unknown>) => {
    const now = Date.now();
    const key = `${eventTitle}:${stableStringify(params)}`;
    const lastSentAt = recentMarketingEvents.get(key);

    if (lastSentAt != null && now - lastSentAt < DUPLICATE_EVENT_WINDOW_MS) {
        return false;
    }

    recentMarketingEvents.set(key, now);

    if (recentMarketingEvents.size > 100) {
        recentMarketingEvents.forEach((sentAt, storedKey) => {
            if (now - sentAt >= DUPLICATE_EVENT_WINDOW_MS) recentMarketingEvents.delete(storedKey);
        });
    }

    return true;
};

export const prepareMarketingEventObject = (partner: Partner, partnerUser?: PartnerUser) => {
    const name = partnerUser?.name || '';
    const firstName = name.split(' ')[0] || '';
    const lastName = name.substring(firstName.length).trim() || '';

    const partnerUserId = getPartnerUserId(partnerUser);

    return {
        company_id: getCompanyId(partner),
        partner_user_id: partnerUserId,
        partnerUserId,
        partnerId: partner.reference,
        brandName: partner.name,
        firstName,
        lastName,
        ...(partner.employeeCount ? { businessSize: partner.employeeCount } : {}),
        ...(partner.industriesAndSubIndustriesEn ? { industry: partner.industriesAndSubIndustriesEn } : {}),
        ...(partner.leadSource ? { leadSource: partner.leadSource } : {}),
    };
};

export const trackMarketingEventDatalayer = ({
    eventTitle,
    partner,
    partnerUser,
    metadata,
}: {
    eventTitle: string;
    partner?: Partner;
    partnerUser?: PartnerUser;
    metadata?: Record<string, unknown>;
}) => {
    window['dataLayer'] = window['dataLayer'] || [];

    const eventObject: Record<string, unknown> = {
        event: eventTitle,
    };

    if (partner) {
        Object.assign(eventObject, prepareMarketingEventObject(partner, partnerUser));
    }

    if (metadata) {
        Object.assign(eventObject, metadata);
    }

    return window['dataLayer']?.push(eventObject);
};

export const trackMarketingEvent = ({
    eventTitle,
    partner,
    partnerUser,
    metadata,
}: {
    eventTitle: string;
    partner?: Partner;
    partnerUser?: PartnerUser;
    metadata?: Record<string, unknown>;
}) => {
    const eventMetadata = withLastUtmMetadata(eventTitle, metadata);
    const eventParams: Record<string, unknown> = {};

    if (partner) {
        Object.assign(eventParams, prepareMarketingEventObject(partner, partnerUser));
    }

    if (eventMetadata) {
        Object.assign(eventParams, eventMetadata);
    }

    if (!claimMarketingEventSend(eventTitle, eventParams)) return;

    // gtag() already pushes onto dataLayer, which GTM and Clarity both read.
    // A second dataLayer.push of the same name is what showed up as the duplicate pair.
    const gtag = window.gtag;
    if (gtag) {
        gtag('event', eventTitle, eventParams);
        return;
    }

    trackMarketingEventDatalayer({
        eventTitle,
        partner,
        partnerUser,
        metadata: eventMetadata,
    });
};

/**
 * Tracks a paywall / plans-page funnel event, always attaching the shared
 * touchpoint, user_state and surface parameters.
 */
export const trackFunnelEvent = ({
    eventTitle,
    partner,
    partnerUser,
    source,
    planCategory,
    qty,
    extra,
}: {
    eventTitle: string;
    partner?: Partner | null;
    partnerUser?: PartnerUser | null;
    source?: FrontendPaymentSource | string | null;
    planCategory?: SubscriptionPlanCategoryEnum | string | null;
    qty?: number;
    extra?: Record<string, unknown>;
}) => {
    const companyId = getCompanyId(partner ?? undefined);

    trackMarketingEvent({
        eventTitle,
        partner: partner ?? undefined,
        partnerUser: partnerUser ?? undefined,
        metadata: buildFunnelParams({
            source,
            partner,
            planCategory,
            qty,
            extra: {
                ...(companyId ? { company_id: companyId } : {}),
                ...extra,
            },
        }),
    });
};

export const getPartnerMarketingContext = () => {
    const profile = store.getState().partnerProfile?.partnerProfile;
    return {
        partner: profile?.partners?.[0],
        partnerUser: profile?.partnerUser,
    };
};

export const trackSupportChatInitiated = () => {
    const { partner, partnerUser } = getPartnerMarketingContext();

    trackMarketingEvent({
        eventTitle: MarketingEvents.SUPPORT_CHAT_INITIATED,
        partner,
        partnerUser,
        metadata: {
            page_source: window.location.pathname.split('/')[1] || 'dashboard',
            subscription_status: partner?.currentSubscription?.subscriptionStatus,
        },
    });
};

export const trackRegistrationAbandoned = (stepReached: string, timeOnPageSec: number) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.REGISTRATION_ABANDONED,
        metadata: buildRegistrationAbandonedMetadata(stepReached, timeOnPageSec),
    });
};

export const trackCvDatabaseSearched = ({
    partner,
    partnerUser,
    searchQuery,
    filtersApplied,
    resultsCount,
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    searchQuery: string;
    filtersApplied: string;
    resultsCount: number;
}) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.CV_DATABASE_SEARCHED,
        partner,
        partnerUser,
        metadata: buildCvDatabaseSearchedMetadata({ searchQuery, filtersApplied, resultsCount }),
    });
};

export const trackAiInterviewRequested = ({
    partner,
    partnerUser,
    candidateId,
    interviewType,
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    candidateId: string;
    interviewType: 'audio' | 'text';
}) => {
    if (!candidateId) return;

    trackMarketingEvent({
        eventTitle: MarketingEvents.AI_INTERVIEW_REQUESTED,
        partner,
        partnerUser,
        metadata: buildAiInterviewRequestedMetadata(candidateId, interviewType),
    });
};

export const trackSubscriptionInitiated = ({
    partner,
    partnerUser,
    plan,
    billingCycle,
    jobsCount,
    lang = 'en',
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    plan: SubscriptionPlanInterface;
    billingCycle: SubscriptionBillingCycleEnum;
    jobsCount: number;
    lang?: 'en' | 'ar';
}) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.SUBSCRIPTION_INITIATED,
        partner,
        partnerUser,
        metadata: buildSubscriptionMetadata(plan, billingCycle, jobsCount, lang),
    });
};

export const trackPlanSelected = ({
    partner,
    partnerUser,
    plan,
    billingCycle,
    jobsCount,
    lang = 'en',
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    plan: SubscriptionPlanInterface;
    billingCycle: SubscriptionBillingCycleEnum;
    jobsCount: number;
    lang?: 'en' | 'ar';
}) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.PLAN_SELECTED,
        partner,
        partnerUser,
        metadata: buildPlanSelectedMetadata(plan, billingCycle, jobsCount, lang),
    });
};

export const trackPurchaseResult = async ({
    paymentStatus,
    paymentRefId,
    partner,
    partnerUser,
    preparePurchaseData,
    subscriptionPlans,
    errorType,
    isPma,
}: {
    paymentStatus: PaymentStatusEnum | PaymentStepsEnum;
    paymentRefId?: string;
    partner?: Partner;
    partnerUser?: PartnerUser;
    preparePurchaseData?: SubscriptionPurchase;
    subscriptionPlans?: SubscriptionPlanInterface[];
    errorType?: string;
    isPma?: boolean;
}) => {
    const lang = localStorage.getItem('i18nextLng')?.includes('en') ? 'en' : 'ar';
    const selectedPlan = subscriptionPlans?.find((plan) => plan.externalId === preparePurchaseData?.planId);
    const subscriptionMetadata = preparePurchaseData
        ? buildSubscriptionMetadataFromPurchase(preparePurchaseData, selectedPlan, lang)
        : {};

    if (paymentStatus === PaymentStatusEnum.SUCCESS) {
        if (isPma) {
            const ftPartnerSubscriptionInfo = localStorage.getItem('specialLinkftPartnerSubscriptionInfo')
                ? JSON.parse(localStorage.getItem('specialLinkftPartnerSubscriptionInfo')!)
                : null;
            trackMarketingEvent({
                eventTitle: MarketingEvents.PURCHASE_COMPLETED,
                metadata: {
                    ...subscriptionMetadata,
                    transaction_id: paymentRefId,
                    is_first_subscription: !ftPartnerSubscriptionInfo?.hasSubscribedAtLeastOnce,
                },
            });
        } else if (partner?.hasSubscribedAtLeastOnce) {
            trackMarketingEvent({
                eventTitle: MarketingEvents.SUBSCRIPTION_REPURCHASED,
                partner,
                partnerUser,
                metadata: {
                    ...subscriptionMetadata,
                    transaction_id: paymentRefId,
                    is_renewal: true,
                },
            });
        } else {
            trackMarketingEvent({
                eventTitle: MarketingEvents.PURCHASE_COMPLETED,
                partner,
                partnerUser,
                metadata: {
                    ...subscriptionMetadata,
                    transaction_id: paymentRefId,
                    email: await hashPiiValue(partnerUser?.email),
                    mobile_number: partnerUser?.mobile
                        ? await hashPiiValue(normalizeMobileToE164(partnerUser.mobile))
                        : undefined,
                    is_first_subscription: true,
                },
            });
        }
    } else if (paymentStatus === PaymentStatusEnum.FAILED || paymentStatus === PaymentStepsEnum.PAYMENT_FAILED) {
        trackMarketingEvent({
            eventTitle: MarketingEvents.PURCHASE_FAILED,
            partner,
            partnerUser,
            metadata: {
                ...subscriptionMetadata,
                ...(errorType ? { error_type: errorType } : {}),
                ...(paymentRefId ? { transaction_id: paymentRefId } : {}),
            },
        });
    }
};

export const trackApplicantCalled = ({
    partner,
    partnerUser,
    applicant,
    jobId,
    contactType,
    isRecommended,
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    applicant?: ApplicantListItem;
    jobId?: string;
    contactType: 'mobile' | 'email';
    isRecommended?: boolean;
}) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.APPLICANTS_CALLED,
        partner,
        partnerUser,
        metadata: buildApplicantActionMetadata({
            applicant,
            jobId,
            companyId: getCompanyId(partner),
            extra: {
                contact_type: contactType,
                ...(isRecommended != null ? { is_recommended: isRecommended } : {}),
            },
        }),
    });
};

export const trackApplicantUnlocked = ({
    partner,
    partnerUser,
    applicant,
    jobId,
    isRecommended,
    unlockMethod,
    unlocksRemaining,
}: {
    partner?: Partner;
    partnerUser?: PartnerUser;
    applicant?: ApplicantListItem;
    jobId?: string;
    isRecommended?: boolean;
    unlockMethod?: string;
    unlocksRemaining?: number;
}) => {
    trackMarketingEvent({
        eventTitle: MarketingEvents.APPLICANTS_UNLOCKED,
        partner,
        partnerUser,
        metadata: buildApplicantActionMetadata({
            applicant,
            jobId,
            companyId: getCompanyId(partner),
            extra: {
                ...(isRecommended != null ? { is_recommended: isRecommended } : {}),
                ...(unlockMethod ? { unlock_method: unlockMethod } : {}),
                ...(unlocksRemaining != null ? { unlocks_remaining: unlocksRemaining } : {}),
            },
        }),
    });
};
